/**
 * Money is stored as integers — never floats. One unit = 1/10,000 of the
 * currency (four decimal places).
 *
 * Why 4dp and not cents: this business sells buttons. Real unit prices in the
 * legacy data run to four decimals (0.0122, 0.0280, 0.005) against MOQs of
 * 5,000–16,000 pieces. Storing cents would round 0.005 → 0.01 (100% error);
 * measured across the full legacy dataset it distorts 986 of 1,648 line items
 * and 10.33% of total money value. Totals are still PRESENTED at 2dp — the
 * extra resolution exists so unit prices survive storage, not to show noise.
 */

/** Integer units per one unit of currency. */
export const MONEY_SCALE = 10_000;

/** Decimal places the storage scale can represent exactly. */
export const MONEY_DP = 4;

/** Round a major-unit value to N decimal places (half-up). */
export function roundTo(value: number, dp: number): number {
  const f = 10 ** dp;
  return Math.round((value + Number.EPSILON) * f) / f;
}

/** Major units → integer storage units (4dp). */
export function toMinor(major: number): number {
  return Math.round(roundTo(major, MONEY_DP) * MONEY_SCALE);
}

/** Integer storage units → major units. */
export function toMajor(minor: number): number {
  return minor / MONEY_SCALE;
}

/** Line amount = qty × unit price, rounded at 4dp like the legacy app. */
export function lineAmountMinor(quantity: number, unitPriceMinor: number): number {
  return toMinor(roundTo(quantity * toMajor(unitPriceMinor), MONEY_DP));
}

/** Document total, rounded to 2dp — the presentation-grade figure. */
export function totalMinor(sumOfLinesMinor: number): number {
  return toMinor(roundTo(toMajor(sumOfLinesMinor), 2));
}

/**
 * Proforma-invoice grand total, matching the legacy tfoot arithmetic:
 *   base  = round2(Σ line amounts + upcharge)
 *   total = base − round2(base × discount%)
 *
 * `discount` is stored as percent × 100 (12.5% → 1250) — it is NOT money and
 * must never go through toMinor/MONEY_SCALE. `upcharge` IS money (minor units).
 * Shared so the API and the editor's live preview cannot drift apart.
 */
export function piTotalMinor(sumOfLinesMinor: number, upchargeMinor = 0, discountPctX100 = 0): number {
  const base = totalMinor(sumOfLinesMinor + upchargeMinor);
  if (!discountPctX100) return base;
  return base - totalMinor((base * discountPctX100) / 10_000);
}

/** Purchase totals: lines at 4dp, subtotal/tax/grand total at 2dp. */
export function poTotalsMinor(lineSum: number, taxPctX100 = 0, shippingMinor = 0) {
  const subtotal = totalMinor(lineSum);
  const tax = totalMinor(subtotal * taxPctX100 / 10_000);
  return { subtotal, tax, total: totalMinor(subtotal + tax + shippingMinor) };
}
